const WheelTorque = () => {
  return (
    <div className="bg-[#fff] w-full flex flex-col">
      <div
        className="relative w-full bg-cover bg-center h-[550px] flex items-center justify-center"
        style={{
          backgroundImage: "url('/images/wheel-torque.png')",
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative text-center px-6 md:px-12 lg:px-20 max-w-4xl">
          <h1 className="text-white text-3xl md:text-5xl font-bold leading-tight mb-4">
            Understanding Wheel Torque: Why Tightening Matters More Than You Think
          </h1>
          <p className="text-gray-200 text-lg md:text-xl">
            Wheel torque isn’t just about tightening lug nuts—it’s about ensuring safety,
            stability, and long-term performance. Learn why correct torque matters and how
            professionals get it right every time.
          </p>
        </div>
      </div>

      <section className="w-full px-4 py-10 md:px-12 lg:px-24 xl:px-32">
        <div className="mx-auto max-w-4xl text-gray-800 leading-relaxed">
          <p className="mb-6">
            When it comes to tire and wheel installation, one of the most overlooked yet
            critical steps is applying the correct torque to the lug nuts. Over-tightening
            or under-tightening can cause serious problems—from warped brake rotors to wheel
            separation while driving.
            At <strong>Express Tire Works</strong>, we use precision torque tools to ensure
            every wheel is secured correctly according to manufacturer standards.
            Here’s why wheel torque matters more than you might think.
          </p>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              1. What Is Wheel Torque?
            </h2>
            <p>
              Wheel torque refers to the amount of rotational force applied when tightening
              each lug nut on a wheel. Measured in pound-feet (lb-ft) or Newton-meters (Nm),
              the correct torque is essential for:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li><strong>Even pressure distribution:</strong> Ensures the wheel sits flat against the hub.</li>
              <li><strong>Wheel security:</strong> Prevents the wheel from loosening over time.</li>
              <li><strong>Brake system stability:</strong> Avoids rotor warping caused by uneven tightening.</li>
              <li><strong>Long-term safety:</strong> Keeps all components working as designed.</li>
            </ul>
            <p>
              Every vehicle has a specific torque requirement, and following it is crucial
              for safety and performance.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              2. What Happens If Lug Nuts Are Over-Tightened?
            </h2>
            <p>
              Many people believe tighter is better—but over-tightening lug nuts is one of
              the biggest mistakes during wheel installation. It can cause:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li><strong>Warped brake rotors:</strong> Leading to vibration during braking.</li>
              <li><strong>Stripped lug nuts:</strong> Making future removal difficult or impossible.</li>
              <li><strong>Damaged wheel studs:</strong> Increasing the risk of wheel failure.</li>
              <li><strong>Cracked alloy wheels:</strong> Especially common in lightweight rims.</li>
            </ul>
            <p>
              Excessive force stretches the wheel studs beyond their intended limit, 
              weakening them permanently.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              3. What Happens If Lug Nuts Are Under-Tightened?
            </h2>
            <p>
              Under-tightening is just as dangerous as over-tightening. When lug nuts are
              not tightened enough, it can lead to:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>Loose wheels while driving</li>
              <li>Vibration at high speeds</li>
              <li>Uneven pressure on the wheel hub</li>
              <li>Wheel separation in extreme cases</li>
            </ul>
            <p>
              Even a slightly loose wheel can quickly escalate into a major safety hazard.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              4. Why Manufacturers Specify Exact Torque Values
            </h2>
            <p>
              Every vehicle comes with a recommended torque specification unique to its wheel
              design and stud size. These torque values exist because:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>Different metals expand at different rates</li>
              <li>Wheel bolt patterns vary in strength</li>
              <li>Brake rotors require even pressure to prevent warping</li>
              <li>Alloy wheels are more sensitive to stress</li>
            </ul>
            <p>
              Following these specs ensures optimal performance, safety, and wheel longevity.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              5. Tools Professionals Use for Accurate Torque
            </h2>
            <p>
              At <strong>Express Tire Works</strong>, our technicians use precision-calibrated tools
              to apply the exact torque required. These include:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li><strong>Torque Wrenches:</strong> Digital or manual tools for controlled tightening.</li>
              <li><strong>Torque Sticks:</strong> Used with impact guns to prevent over-tightening.</li>
              <li><strong>Cross-Pattern Techniques:</strong> Ensures even seating of the wheel.</li>
              <li><strong>Re-Torque Checks:</strong> A safety step performed after installation.</li>
            </ul>
            <p>
              These methods guarantee that every wheel is tightened safely and evenly.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              6. Why You Should Re-Torque Your Wheels After Driving
            </h2>
            <p>
              After your wheels have been removed or replaced, professionals often recommend
              a re-torque check after the first <strong>50–100 miles</strong>. This helps ensure:
            </p>
            <ul className="list-disc pl-6 mt-2 mb-4">
              <li>Lug nuts haven’t loosened due to seating-in</li>
              <li>Wheel studs remain evenly stressed</li>
              <li>Long-term safety and reliability</li>
            </ul>
            <p>
              It’s a quick yet essential step that extends the life of your wheel assembly.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-black text-2xl font-semibold mb-3 pl-4 border-l-4 border-yellow-500">
              Final Thoughts
            </h2>
            <p>
              Wheel torque is a critical but often underestimated part of tire installation.
              Whether it’s preventing warped rotors, avoiding loose wheels, or ensuring safe
              driving at high speeds, proper torque is essential.
            </p>
            <p className="mt-4">
              Visit <strong>Express Tire Works</strong> for professional tire installation,
              precision torque services, and expert care to keep your vehicle safe on the road.
            </p>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-3">
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Wheel Torque</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Tire Installation</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Vehicle Safety</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Brake Systems</p>
          <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Automotive Maintenance</p>
        </div>
      </section>
    </div>
  );
};

export default WheelTorque;